import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import { CheckCircle, Plus, Trash2, Calendar as CalendarIcon, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { useToast } from '@/hooks/use-toast';
import {
  captationService,
  CaptationRequest,
  ApproveCaptationDto,
  RejectCaptationDto,
} from '@/features/captation/services/captation.service';
import { calendarService } from '@/features/calendar/services/calendar.service';
import { CaptationNotificationUsersDialog } from '@/features/captation/components/CaptationNotificationUsersDialog';

export const Route = createFileRoute('/_authenticated/calendar-captation-approvals')({
  component: CalendarCaptationApprovalsPage,
});

function CalendarCaptationApprovalsPage() {
  return (
    <PermissionGate permission={Permission.CAPTATION_APPROVE}>
      <CalendarCaptationApprovalsContent />
    </PermissionGate>
  );
}

function CalendarCaptationApprovalsContent() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selected, setSelected] = useState<CaptationRequest | null>(null);
  const [editPayload, setEditPayload] = useState<ApproveCaptationDto>({});
  const [rejectionReason, setRejectionReason] = useState('');

  const { data: pending = [], isLoading } = useQuery({
    queryKey: ['captation-pending'],
    queryFn: () => captationService.listPending(),
  });

  const pendingSorted = useMemo(
    () =>
      [...pending].sort(
        (a, b) =>
          new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime(),
      ),
    [pending],
  );

  const approveMutation = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto?: ApproveCaptationDto }) =>
      captationService.approve(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['captation-pending'] });
      queryClient.invalidateQueries({ queryKey: ['captation-list'] });
      setApproveOpen(false);
      setSelected(null);
      setEditPayload({});
      toast({
        title: 'Captação aprovada',
        description: 'O evento foi criado no Google Calendar.',
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: RejectCaptationDto }) =>
      captationService.reject(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['captation-pending'] });
      queryClient.invalidateQueries({ queryKey: ['captation-list'] });
      queryClient.invalidateQueries({ queryKey: ['captation-my'] });
      setRejectOpen(false);
      setSelected(null);
      setRejectionReason('');
      toast({
        title: 'Captação reprovada',
        description: 'O solicitante foi notificado com o motivo.',
      });
    },
  });

  const openApprove = (req: CaptationRequest) => {
    setSelected(req);
    setEditPayload({
      approvedName: req.name,
      approvedScheduledAt: req.scheduledAt,
      approvedEmails: [...(req.emails || [])],
    });
    setApproveOpen(true);
  };

  const openReject = (req: CaptationRequest) => {
    setSelected(req);
    setRejectionReason('');
    setRejectOpen(true);
  };

  const handleApprove = () => {
    if (!selected) return;
    const emailsFiltered = (editPayload.approvedEmails ?? selected.emails ?? [])
      .map((e) => e.trim())
      .filter(Boolean);
    if (emailsFiltered.length === 0) {
      toast({
        title: 'E-mail obrigatório',
        description: 'Informe pelo menos um e-mail.',
        variant: 'destructive',
      });
      return;
    }
    const hasEdits =
      editPayload.approvedName !== undefined ||
      editPayload.approvedScheduledAt !== undefined ||
      (editPayload.approvedEmails !== undefined &&
        editPayload.approvedEmails.length > 0);
    approveMutation.mutate({
      id: selected.id,
      dto: hasEdits
        ? {
            ...editPayload,
            approvedEmails: emailsFiltered,
          }
        : undefined,
    });
  };

  const handleReject = () => {
    if (!selected) return;
    const reason = rejectionReason.trim();
    if (!reason) {
      toast({
        title: 'Motivo obrigatório',
        description: 'Informe o motivo da reprovação.',
        variant: 'destructive',
      });
      return;
    }
    rejectMutation.mutate({
      id: selected.id,
      dto: { rejectionReason: reason },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Captação aprovações</h1>
        <Button variant="outline" onClick={() => setSettingsOpen(true)}>
          Configurar notificados
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Solicitações pendentes</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground">Carregando...</p>
          ) : pendingSorted.length === 0 ? (
            <p className="text-muted-foreground">
              Nenhuma solicitação pendente de aprovação.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Data e hora</TableHead>
                  <TableHead>Solicitante</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingSorted.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell>
                      {format(
                        new Date(r.scheduledAt),
                        "dd/MM/yyyy 'às' HH:mm",
                        { locale: ptBR },
                      )}
                    </TableCell>
                    <TableCell>
                      {r.requestedByUser?.email ?? r.requestedBy}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openReject(r)}
                        >
                          <XCircle className="mr-2 h-4 w-4" />
                          Reprovar
                        </Button>
                        <Button size="sm" onClick={() => openApprove(r)}>
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Aprovar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {selected && (
        <ApproveDialog
          open={approveOpen}
          onOpenChange={setApproveOpen}
          request={selected}
          editPayload={editPayload}
          setEditPayload={setEditPayload}
          onConfirm={handleApprove}
          isPending={approveMutation.isPending}
        />
      )}

      {selected && (
        <RejectDialog
          open={rejectOpen}
          onOpenChange={setRejectOpen}
          request={selected}
          rejectionReason={rejectionReason}
          setRejectionReason={setRejectionReason}
          onConfirm={handleReject}
          isPending={rejectMutation.isPending}
        />
      )}

      <CaptationNotificationUsersDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
      />
    </div>
  );
}

function ApproveDialog({
  open,
  onOpenChange,
  request,
  editPayload,
  setEditPayload,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  request: CaptationRequest;
  editPayload: ApproveCaptationDto;
  setEditPayload: (p: ApproveCaptationDto) => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  const scheduledAt = editPayload.approvedScheduledAt ?? request.scheduledAt;
  const dayStart = useMemo(() => {
    const d = new Date(scheduledAt);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }, [scheduledAt]);
  const dayEnd = useMemo(() => {
    const d = new Date(scheduledAt);
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  }, [scheduledAt]);

  const { data: dayEvents = [] } = useQuery({
    queryKey: ['calendar-events-day', dayStart, dayEnd],
    queryFn: () => calendarService.getEvents(dayStart, dayEnd),
    enabled: open,
  });

  const addEmail = () =>
    setEditPayload({
      ...editPayload,
      approvedEmails: [...(editPayload.approvedEmails ?? request.emails ?? []), ''],
    });
  const removeEmail = (i: number) =>
    setEditPayload({
      ...editPayload,
      approvedEmails: (editPayload.approvedEmails ?? request.emails ?? []).filter(
        (_, idx) => idx !== i,
      ),
    });
  const setEmail = (i: number, v: string) =>
    setEditPayload({
      ...editPayload,
      approvedEmails: (editPayload.approvedEmails ?? request.emails ?? []).map(
        (e, idx) => (idx === i ? v : e),
      ),
    });

  const emails = editPayload.approvedEmails ?? request.emails ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" busy={isPending}>
        <DialogHeader>
          <DialogTitle>Aprovar captação e criar no calendário</DialogTitle>
        </DialogHeader>
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            onConfirm();
          }}
        >
        <ScrollArea className="flex-1 pr-4">
          <div className="grid gap-6 py-4">
            <div className="grid gap-2">
              <Label>Dados da solicitação</Label>
              <p className="text-sm text-muted-foreground">
                Nome: {request.name} • Data:{' '}
                {format(new Date(request.scheduledAt), "dd/MM/yyyy 'às' HH:mm", {
                  locale: ptBR,
                })}
              </p>
              {request.message && (
                <p className="text-sm text-muted-foreground">
                  Mensagem: {request.message}
                </p>
              )}
            </div>

            <div className="grid gap-2">
              <Label>Nome final (editar se necessário)</Label>
              <Input
                value={editPayload.approvedName ?? request.name}
                onChange={(e) =>
                  setEditPayload({ ...editPayload, approvedName: e.target.value })
                }
                placeholder="Nome da captação"
              />
            </div>
            <div className="grid gap-2">
              <Label>Data e hora final (editar se necessário)</Label>
              <Input
                type="datetime-local"
                value={
                  scheduledAt
                    ? format(new Date(scheduledAt), "yyyy-MM-dd'T'HH:mm")
                    : ''
                }
                onChange={(e) =>
                  setEditPayload({
                    ...editPayload,
                    approvedScheduledAt: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : undefined,
                  })
                }
              />
            </div>
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label>E-mails (editar se necessário)</Label>
                <Button type="button" variant="outline" size="sm" onClick={addEmail}>
                  <Plus className="h-4 w-4 mr-1" />
                  Adicionar
                </Button>
              </div>
              {emails.map((email, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(i, e.target.value)}
                    placeholder="email@exemplo.com"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Remover e-mail ${i + 1}`}
                    onClick={() => removeEmail(i)}
                    disabled={emails.length <= 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="grid gap-2">
              <Label className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4" />
                Agenda do dia ({format(new Date(scheduledAt), 'dd/MM/yyyy', { locale: ptBR })})
              </Label>
              <Card className="bg-muted/50">
                <CardContent className="pt-4">
                  {dayEvents.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Nenhum evento neste dia.
                    </p>
                  ) : (
                    <ul className="space-y-2 text-sm">
                      {dayEvents.map((ev) => (
                        <li
                          key={ev.id}
                          className="flex justify-between items-center"
                        >
                          <span>{ev.title}</span>
                          <span className="text-muted-foreground">
                            {format(new Date(ev.startTime), 'HH:mm', {
                              locale: ptBR,
                            })}{' '}
                            –{' '}
                            {format(new Date(ev.endTime), 'HH:mm', {
                              locale: ptBR,
                            })}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </ScrollArea>
        <DialogFooter className="border-t pt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="submit" loading={isPending}>
            Aprovar e criar no calendário
          </Button>
        </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({
  open,
  onOpenChange,
  request,
  rejectionReason,
  setRejectionReason,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  request: CaptationRequest;
  rejectionReason: string;
  setRejectionReason: (v: string) => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  const canConfirm = rejectionReason.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm" busy={isPending}>
        <DialogHeader>
          <DialogTitle>Reprovar captação</DialogTitle>
        </DialogHeader>
        <form
          className="grid gap-4 py-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (canConfirm) onConfirm();
          }}
        >
          <p className="text-sm text-muted-foreground">
            Solicitação: <span className="font-medium text-foreground">{request.name}</span>
            {' · '}
            {format(new Date(request.scheduledAt), "dd/MM/yyyy 'às' HH:mm", {
              locale: ptBR,
            })}
          </p>
          <div className="grid gap-2">
            <Label htmlFor="rejection-reason">Motivo da reprovação *</Label>
            <Textarea
              id="rejection-reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Explique por que a solicitação foi reprovada..."
              rows={4}
              maxLength={2000}
            />
          </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="destructive"
            loading={isPending}
            disabled={!canConfirm}
          >
            Reprovar captação
          </Button>
        </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
